import { useState, useEffect, useRef } from 'react';
import { Navbar } from '../../components/navbar';
import { Footer } from '../../components/footer';
import { SectionTitle } from '../../components/sectionTitle';
import { scrollBodyToTop } from '../../utils/utils';
import AppConfig from '../../utils/appconfig';
import { RegistrationFormData } from '../../types/Registration';
import { EventItem, EventOption } from '../../types/Events';
import tireTracks from '../../images/tire-track.png';
import SuperKlase from '../../images/sygnet.webp';
import { Rejestracja3Form } from './components/rejestracja3Form';
import { EventLanguageSwitcher, EVENT_LANG_STORAGE_KEY } from '../../components/eventLanguageSwitcher';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Leaflet wymaga ręcznego wskazania ikon (CRA nie kopiuje ich automatycznie)
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface EventMapProps {
    coords: string;
    label: string;
}

function EventMap({ coords, label }: EventMapProps) {
    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<L.Map | null>(null);

    useEffect(() => {
        if (!mapRef.current || mapInstanceRef.current) return;

        const [lat, lng] = coords.split(',').map(Number);
        if (isNaN(lat) || isNaN(lng)) return;

        const map = L.map(mapRef.current, {
            center: [lat, lng],
            zoom: 15,
            scrollWheelZoom: false,
        });

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        }).addTo(map);

        L.marker([lat, lng]).addTo(map).bindPopup(label);

        mapInstanceRef.current = map;

        return () => {
            map.remove();
            mapInstanceRef.current = null;
        };
    }, [coords, label]);

    return <div ref={mapRef} className="rejestracja3__map" />;
}

const currentPage: string = 'rejestracja3';

// Opcja bez twardego limitu (has_capacity_limit === false) nadal ma licznik
// current_count (istotny do prezentacji), ale nie ma sensu pokazywać go
// jako "x/y miejsc" — bo max_capacity dla takiej opcji jest tylko wartością
// domyślną, a nie realnym ograniczeniem.
function formatOptionCapacity(opt: EventOption): string {
    if (opt.has_capacity_limit === false) {
        return `${opt.current_count} wybrało · bez limitu miejsc`;
    }
    return `${opt.current_count}/${opt.max_capacity} miejsc`;
}

export function Rejestracja3() {
    const [submitted, setSubmitted] = useState(false);
    const [formVisible, setFormVisible] = useState(false);
    const [expandedEventId, setExpandedEventId] = useState<string | null>(null);

    // Język treści wydarzeń (tylko wydarzenia — reszta strony zostaje po polsku).
    const [lang, setLang] = useState<string>(
        () => localStorage.getItem(EVENT_LANG_STORAGE_KEY) || 'pl'
    );
    const handleLangChange = (next: string) => {
        setLang(next);
        localStorage.setItem(EVENT_LANG_STORAGE_KEY, next);
    };

    const [formData, setFormData] = useState<RegistrationFormData>({
        name: '',
        nickname: '',
        email: '',
        contact: '',
        motorcycleBrand: '',
        registrationType: 'individual',
        groupSize: 1,
        selectedEvents: [],
        selectedOptions: [],
        comment: '',
        shirtSize: '',
        jerseySize: '',
    });

    const [selectedOptions, setSelectedOptions] = useState<Record<string, number[]>>({});
    const [eventOptionsMap, setEventOptionsMap] = useState<Record<string, {
        transport: EventOption[];
        hotel: EventOption[];
    }>>({});

    const [events, setEvents] = useState<EventItem[]>([]);

    // Stan dla przycisku nawigacyjnego
    const [showNavigateBtn, setShowNavigateBtn] = useState(false);
    const navigateTimerRef = useRef<NodeJS.Timeout | null>(null);
    const formRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        document.title = `Zapisz się - ${AppConfig.baseTabTitle}`;
        scrollBodyToTop();

        fetch(`${AppConfig.apiPath}/events.php?lang=${lang}`)
            .then((res) => res.json())
            .then((data) => {
                setEvents(data);

                const optionsPromises = data.map((event: EventItem) =>
                    fetch(`${AppConfig.apiPath}/event.php?id=${event.id}&lang=${lang}`)
                        .then(res => res.json())
                        .then(eventData => ({
                            eventId: event.id,
                            transport: eventData.transport_options || [],
                            hotel: eventData.hotel_options || []
                        }))
                        .catch(() => ({
                            eventId: event.id,
                            transport: [],
                            hotel: []
                        }))
                );

                Promise.all(optionsPromises).then(results => {
                    const map: Record<string, any> = {};
                    results.forEach(r => {
                        map[r.eventId] = {
                            transport: r.transport,
                            hotel: r.hotel
                        };
                    });
                    setEventOptionsMap(map);
                });
            })
            .catch(() => {
                console.error('Błąd pobierania wydarzeń');
            });
    }, [lang]);

    const activeEvents = events.filter((e) => e.active);

    const handleToggleExpand = (eventId: string) => {
        setExpandedEventId((prev) => (prev === eventId ? null : eventId));
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: name === 'groupSize' ? Number(value) : value,
        }));
    };

    const handleSizeChange = (field: 'shirtSize' | 'jerseySize', size: string) => {
        setFormData((prev) => ({
            ...prev,
            [field]: prev[field] === size ? '' : size,
        }));
    };

    const handleEventChange = (eventId: string) => {
        const event = activeEvents.find((e) => e.id === eventId);
        if (event && !event.registrations_open) {
            return;
        }

        const isCurrentlySelected = formData.selectedEvents.includes(eventId);

        if (isCurrentlySelected) {
            // Odznacz wydarzenie
            setFormData((prev) => ({
                ...prev,
                selectedEvents: [],
            }));
            setSelectedOptions((prev) => {
                const { [eventId]: _removed, ...rest } = prev;
                return rest;
            });
            setShowNavigateBtn(false);
            if (navigateTimerRef.current) {
                clearTimeout(navigateTimerRef.current);
                navigateTimerRef.current = null;
            }
        } else {
            // Wybierz wydarzenie
            setFormData((prev) => ({
                ...prev,
                selectedEvents: [eventId],
            }));
            setSelectedOptions({});

            // Pokaż przycisk nawigacyjny
            setShowNavigateBtn(true);
            if (navigateTimerRef.current) {
                clearTimeout(navigateTimerRef.current);
            }
            navigateTimerRef.current = setTimeout(() => {
                setShowNavigateBtn(false);
                navigateTimerRef.current = null;
            }, 760000); // lekko wydłużony czas
        }
    };

    const handleOptionChange = (eventId: string, optionId: number, optionType?: 'transport' | 'hotel') => {
        setSelectedOptions((prev) => {
            const current = prev[eventId] || [];

            if (optionType) {
                const groupIds = (eventOptionsMap[eventId]?.[optionType] || []).map((o) => o.id);
                const withoutGroup = current.filter((id) => !groupIds.includes(id));
                return {
                    ...prev,
                    [eventId]: [...withoutGroup, optionId],
                };
            }

            const isSelected = current.includes(optionId);
            return {
                ...prev,
                [eventId]: isSelected
                    ? current.filter((id) => id !== optionId)
                    : [...current, optionId],
            };
        });
    };

    const handleNavigateToForm = () => {
        if (!formVisible) {
            setFormVisible(true);
            setTimeout(() => {
                if (formRef.current) {
                    formRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                }
            }, 300);
        } else {
            if (formRef.current) {
                formRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }
        }

        setShowNavigateBtn(false);
        if (navigateTimerRef.current) {
            clearTimeout(navigateTimerRef.current);
            navigateTimerRef.current = null;
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (formData.selectedEvents.length === 0) {
            throw new Error('Wybierz przynajmniej jedno wydarzenie.');
        }

        const allSelectedOptionIds = Object.values(selectedOptions).flat();

        const response = await fetch(`${AppConfig.apiPath}/registrations.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                ...formData,
                selectedOptions: allSelectedOptionIds,
                comment: formData.comment,
            }),
        });

        const result = await response.json();

        if (result.error) {
            throw new Error(result.error);
        }

        setSubmitted(true);
    };

    // Komponent przycisku szybkiego zapisu
    const QuickRegisterButton = ({ eventId, isSelected }: { eventId: string; isSelected: boolean }) => {
        const handleQuickRegister = (e: React.MouseEvent) => {
            e.stopPropagation();
            handleEventChange(eventId);
        };

        return (
            <button
                type="button"
                className={`rejestracja3__quick-register-btn${isSelected ? ' rejestracja3__quick-register-btn--selected' : ''}`}
                onClick={handleQuickRegister}
                title={isSelected ? 'Zrezygnuj z tego wydarzenia' : 'Wybierz to wydarzenie'}
            >
                {isSelected ? '✓ Wybrane w formularzu' : '✚ Chce się zapisać'}
            </button>
        );
    };

    // Komponent przycisku nawigacyjnego (pływający)
    const NavigateButton = ({
        isExpanded,
        eventTitle
    }: {
        isExpanded: boolean;
        eventTitle: string;
    }) => {
        if (!showNavigateBtn) return null;

        return (
            <div className="rejestracja3__navigate-wrapper">

                <button
                    type="button"
                    className={`rejestracja3__navigate-btn${isExpanded ? ' rejestracja3__navigate-btn--bottom' : ' rejestracja3__navigate-btn--bottom'}`}
                    onClick={handleNavigateToForm}
                    title={`Przejdź do formularza dla: ${eventTitle}`}
            >
              <img
                  src={SuperKlase}
                  alt="Ilustracja"
                  className="rejestracja3__navigate-img"
              />
                    <span className="rejestracja3__navigate-btn-icon">⬇️</span>
                    {!isExpanded && <span className="rejestracja3__navigate-btn-text">Przejdź do formularza</span>}
                    {isExpanded && <span className="rejestracja3__navigate-btn-label">Przejdź do formularza</span>}
                </button>
            </div>
        );
    };

    return (
        <div className="App theme-light">
            <Navbar currentPage={currentPage} />

            <div className="rejestracja3__wrapper">
                {!submitted && (
                    <div className="rejestracja3__events-bg">
                        <div className="rejestracja3__events-container">
                            <img src={tireTracks} alt="" className="rejestracja3__tire-tracks rejestracja3__tire-tracks--left" loading="lazy" />
                            <img src={tireTracks} alt="" className="rejestracja3__tire-tracks rejestracja3__tire-tracks--right" loading="lazy" />
                            <SectionTitle
                                title={<>DOSTĘPNE <span className="text-color--primary">WYDARZENIA</span></>}
                                customClassName="section-title__wrapper--home"
                            />
                            <EventLanguageSwitcher lang={lang} onChange={handleLangChange} />

                            {activeEvents.length > 0 && (
                                <div className="rejestracja3__event-list">
                                    {activeEvents.map((event) => {
                                        const isExpanded = expandedEventId === event.id;
                                        const isSelected = formData.selectedEvents.includes(event.id);
                                        const hasDetails = event.description_html || event.duration || event.location_name ||
                                            (event.options && event.options.length > 0) ||
                                            (event.transport_options && event.transport_options.length > 0) ||
                                            (event.hotel_options && event.hotel_options.length > 0);

                                        return (
                                            <div
                                                key={event.id}
                                                className="rejestracja3__event-item"
                                                style={{ position: 'relative' }} // kluczowe dla przycisku pływającego
                                            >
                                                {event.image && (
                                                    <img
                                                        src={event.image}
                                                        alt={event.title}
                                                        className="rejestracja3__event-image"
                                                    />
                                                )}
                                                <div className="rejestracja3__event-preview">
                                                    <div className="rejestracja3__event-preview-header">
                                                        <h3>{event.title}</h3>
                                                        <div className="rejestracja3__event-preview-actions" />
                                                    </div>
                                                    {event.date && (
                                                        <div className="rejestracja3__event-date">
                                                            {event.date}
                                                        </div>
                                                    )}
                                                    {!event.registrations_open && (
                                                        <div className="rejestracja3__event-closed-badge">
                                                            Zapisy obecnie niedostępne
                                                        </div>
                                                    )}
                                                    <p>{event.description}</p>

                                                    {hasDetails && (
                                                        <div className="custom-button__link-wrapper">
                                                            <button
                                                                type="button"
                                                                className="custom-button"
                                                                onClick={() => handleToggleExpand(event.id)}
                                                                aria-expanded={isExpanded}
                                                            >
                                                                {isExpanded ? '▲ Zwiń' : '▼ Więcej informacji'}
                                                            </button>
                                                        </div>
                                                    )}

                                                    {event.registrations_open && (
                                                        <QuickRegisterButton
                                                            eventId={event.id}
                                                            isSelected={isSelected}
                                                        />
                                                    )}
                                                </div>

                                                {/* Przycisk nawigacyjny POD wydarzeniem (gdy zwinięte) */}
                                                {isSelected && !isExpanded && (
                                                    <div className="rejestracja3__event-navigate-bottom">
                                                        <NavigateButton
                                                            isExpanded={false}
                                                            eventTitle={event.title}
                                                        />
                                                    </div>
                                                )}

                                                {isExpanded && (
                                                    <div className="rejestracja3__event-details">
                                                        {/* Przycisk pływający w prawym górnym rogu */}
                                                        {isSelected && (
                                                            <NavigateButton
                                                                isExpanded={true}
                                                                eventTitle={event.title}
                                                            />
                                                        )}

                                                        {event.description_html && (
                                                            <div className="rejestracja3__detail-card">
                                                                <span className="rejestracja3__detail-card-label">Szczegółowy opis</span>
                                                                <div
                                                                    className="rejestracja3__detail-html"
                                                                    dangerouslySetInnerHTML={{ __html: event.description_html }}
                                                                />
                                                            </div>
                                                        )}
                                                        {/* reszta szczegółów bez zmian */}
                                                        {event.duration && (
                                                            <div className="rejestracja3__detail-card">
                                                                <span className="rejestracja3__detail-card-label">Czas trwania</span>
                                                                <span>{event.duration}</span>
                                                            </div>
                                                        )}
                                                        {event.location_name && (
                                                            <div className="rejestracja3__detail-card">
                                                                <span className="rejestracja3__detail-card-label">Lokalizacja</span>
                                                                <span>{event.location_name}</span>
                                                                {event.location_coords && (
                                                                    <EventMap
                                                                        coords={event.location_coords}
                                                                        label={event.location_name}
                                                                    />
                                                                )}
                                                            </div>
                                                        )}
                                                        {event.options && event.options.length > 0 && (
                                                            <div className="rejestracja3__detail-card">
                                                                <span className="rejestracja3__detail-card-label">Opcje</span>
                                                                <ul className="rejestracja3__detail-options">
                                                                    {event.options.map((opt, i) => (
                                                                        <li key={i}>{opt}</li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        )}
                                                        {event.transport_options && event.transport_options.length > 0 && (
                                                            <div className="rejestracja3__detail-card">
                                                                <span className="rejestracja3__detail-card-label">🚌 Transport</span>
                                                                <ul className="rejestracja3__detail-options">
                                                                    {event.transport_options.map((opt) => (
                                                                        <li key={opt.id}>
                                                                            {opt.option_name} ({formatOptionCapacity(opt)})
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        )}
                                                        {event.hotel_options && event.hotel_options.length > 0 && (
                                                            <div className="rejestracja3__detail-card">
                                                                <span className="rejestracja3__detail-card-label">🏨 Nocleg</span>
                                                                <ul className="rejestracja3__detail-options">
                                                                    {event.hotel_options.map((opt) => (
                                                                        <li key={opt.id}>
                                                                            {opt.option_name} ({formatOptionCapacity(opt)})
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Ref dla formularza */}
                <div ref={formRef}>
                    <Rejestracja3Form
                        formData={formData}
                        formVisible={formVisible}
                        submitted={submitted}
                        activeEvents={activeEvents}
                        selectedOptions={selectedOptions}
                        eventOptionsMap={eventOptionsMap}
                        onInputChange={handleInputChange}
                        onEventChange={handleEventChange}
                        onOptionChange={handleOptionChange}
                        onSizeChange={handleSizeChange}
                        onSubmit={handleSubmit}
                        onToggle={() => setFormVisible(!formVisible)}
                    />
                </div>
            </div>

            <Footer currentPage={currentPage} />
        </div>
    );
}
